import { TextField, Chip } from "@mui/material";
import { X, CheckCircle, Clock } from "lucide-react";
import React from "react";

const WorkOrderGeneral = ({ WorkOrderData, setWorkOrderData }) => {
  const status = {
    approval: "Approved",
    currentStatus: "Normal",
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 mt-2 p-3">
      <div className="lg:col-span-3 col-span-1 flex flex-col gap-2">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">WO No:</span>
            <span className="text-red-600 font-bold text-base leading-tight">
              {WorkOrderData?.documentNo || "-"}
            </span>
          </div>
          <TextField
            label="Date"
            type="date"
            variant="outlined"
            size="small"
            value={
              WorkOrderData?.purchaseDate ||
              new Date().toISOString().split("T")[0]
            }
            onChange={(e) =>
              setWorkOrderData({
                ...WorkOrderData,
                purchaseDate: e.target.value,
              })
            }
            InputLabelProps={{ shrink: true }}
            sx={{
              minWidth: "140px",
              "& .MuiInputBase-root": { height: "36px" },
            }}
          />
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-2 shadow-sm">
          <div className="grid grid-cols-3 gap-2">
            <TextField
              label="SO No"
              variant="outlined"
              size="small"
              fullWidth
              value={WorkOrderData?.soNo || "-"}
              onChange={(e) =>
                setWorkOrderData({
                  ...WorkOrderData,
                  soNo: e.target.value,
                })
              }
              InputProps={{ readOnly: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                  height: "36px",
                  fontSize: "0.875rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
            />
            <TextField
              label="Quot No"
              variant="outlined"
              size="small"
              fullWidth
              value={WorkOrderData?.quotationNo || "-"}
              onChange={(e) =>
                setWorkOrderData({
                  ...WorkOrderData,
                  quotationNo: e.target.value,
                })
              }
              InputProps={{ readOnly: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                  height: "36px",
                  fontSize: "0.875rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
            />
            <TextField
              label="Lead No"
              variant="outlined"
              size="small"
              fullWidth
              value={WorkOrderData?.leadNo || "-"}
              onChange={(e) =>
                setWorkOrderData({
                  ...WorkOrderData,
                  leadNo: e.target.value,
                })
              }
              InputProps={{ readOnly: true }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                  height: "36px",
                  fontSize: "0.875rem",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "0.75rem",
                },
              }}
            />
          </div>
        </div>

        {/* Status - Inline */}
        <div className="bg-white border border-gray-200 rounded-lg px-3 py-2 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            {/* Approval */}
            <div className="flex items-center gap-1.5">
              <CheckCircle
                className={`w-4 h-4 ${
                  status.approval === "Approved"
                    ? "text-green-500"
                    : "text-red-500"
                }`}
              />
              <Chip
                label={status.approval}
                size="small"
                sx={{
                  backgroundColor:
                    status.approval === "Approved" ? "#22c55e" : "#ef4444",
                  color: "white",
                  fontWeight: 600,
                  height: "24px",
                  fontSize: "0.75rem",
                }}
              />
            </div>

            <div className="h-6 w-px bg-gray-300 lg:flex hidden"></div>

            {/* Status with Label */}
            <div className="flex items-center gap-1.5">
              <span className="text-xs font-medium text-gray-500">Status:</span>
              <Clock className="w-4 h-4 text-blue-500" />
              <Chip
                label={status.currentStatus}
                size="small"
                sx={{
                  backgroundColor:
                    status.currentStatus === "Normal" ? "#dbeafe" : "#fef3c7",
                  color:
                    status.currentStatus === "Normal" ? "#1e40af" : "#92400e",
                  fontWeight: 600,
                  height: "24px",
                  fontSize: "0.75rem",
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Center Section: 2-column MUI fields */}
      <div className="lg:col-span-5 col-span-1">
        <div className="grid grid-cols-2 gap-3">
          <TextField
            label="TRN"
            variant="outlined"
            size="small"
            fullWidth
            value={WorkOrderData?.trn || ""}
            onChange={(e) =>
              setWorkOrderData({
                ...WorkOrderData,
                trn: e.target.value,
              })
            }
          />
          <TextField
            label="LPO"
            variant="outlined"
            size="small"
            fullWidth
            value={WorkOrderData?.lpo || ""}
            onChange={(e) =>
              setWorkOrderData({
                ...WorkOrderData,
                lpo: e.target.value,
              })
            }
          />
          <TextField
            label="Remarks"
            variant="outlined"
            size="small"
            fullWidth
            value={WorkOrderData?.remarks || ""}
            onChange={(e) =>
              setWorkOrderData({
                ...WorkOrderData,
                remarks: e.target.value,
              })
            }
          />
          <TextField
            label="Area Code"
            variant="outlined"
            size="small"
            fullWidth
            value={WorkOrderData?.areaCode || ""}
            onChange={(e) =>
              setWorkOrderData({
                ...WorkOrderData,
                areaCode: e.target.value,
              })
            }
          />
          <TextField
            label="Shipment"
            variant="outlined"
            size="small"
            fullWidth
            value={WorkOrderData?.shipment || ""}
            onChange={(e) =>
              setWorkOrderData({
                ...WorkOrderData,
                shipment: e.target.value,
              })
            }
          />
          <TextField
            label="Origin"
            variant="outlined"
            size="small"
            fullWidth
            value={WorkOrderData?.origin || ""}
            onChange={(e) =>
              setWorkOrderData({
                ...WorkOrderData,
                origin: e.target.value,
              })
            }
          />
        </div>
      </div>

      <div className="lg:col-span-4 col-span-1">
        <div className="bg-gradient-to-r from-slate-100 to-gray-100 border border-gray-200 rounded-lg rounded-tr-none p-2.5 shadow-sm relative">
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-3">
              <div className="flex-1 min-w-0">
                <h4
                  title={WorkOrderData?.customerName}
                  className="font-semibold text-sm text-teal-700 "
                >
                  {WorkOrderData?.customerName}{" "}
                </h4>
              </div>
              <div className="flex items-center gap-1 px-2 py-1 bg-blue-600 rounded-full font-medium flex-shrink-0">
                <span className="text-white text-xs">Ac Dr:</span>
                <span className="text-blue-600 bg-white px-2 py-0.5 rounded-full text-xs font-semibold">
                  {WorkOrderData?.customerAccount || 0}
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-1 px-2.5 py-1 rounded-xl bg-yellow-500 text-xs font-medium">
                <span className="text-white">Cr Limit:</span>
                <span className="font-semibold text-white">
                  {WorkOrderData?.creditLimit}
                </span>
              </div>

              <div
                className={`flex items-center gap-1 px-2.5 py-1 rounded-xl text-xs font-medium ${
                  WorkOrderData?.availableLimit >= 0
                    ? "bg-green-600 text-white"
                    : "bg-red-600 text-white"
                }`}
              >
                <span>Avl Limit:</span>
                <span className="font-semibold">
                  {WorkOrderData?.availableLimit || 0}
                </span>
              </div>
            </div>
          </div>
          {/* <button
            onClick={() => {
              //   dummyCustomer(null);
            }}
            className="absolute -top-1.5 border -right-1.5 text-gray-400 hover:text-gray-500 bg-white hover:bg-gray-50 p-1 rounded-full transition-all duration-200 flex-shrink-0"
            title="Remove Selected customer"
          >
            <X className="w-3.5 h-3.5" />
          </button> */}
        </div>
        <div className=" flex items-center gap-3 mt-3">
          <TextField
            label="Terms"
            variant="outlined"
            size="small"
            fullWidth
            value={WorkOrderData?.terms || ""}
            onChange={(e) =>
              setWorkOrderData({
                ...WorkOrderData,
                terms: e.target.value,
              })
            }
          />
          <TextField
            label="Delivery"
            variant="outlined"
            size="small"
            fullWidth
            value={WorkOrderData?.delivery || ""}
            onChange={(e) =>
              setWorkOrderData({
                ...WorkOrderData,
                delivery: e.target.value,
              })
            }
          />
        </div>
      </div>
    </div>
  );
};

export default WorkOrderGeneral;
